/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Sparkles, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Full-screen "waiting room" shown before a visit can actually be joined.
 *
 * Extracted from VideoRoomPage.jsx (Phase 8 refactor) to keep VideoRoomPage
 * focused on the actual call. This component has no data-fetching logic —
 * the parent passes the appointment + a `onCheckAgain` handler that reloads
 * the room status.
 */
export const WaitingRoom = ({ appt, onCheckAgain }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const [countdown, setCountdown] = useState('');
  const [tip, setTip] = useState(0);
  const tips = React.useMemo(
    () => [t('waitTip1'), t('waitTip2'), t('waitTip3'), t('waitTip4'), t('waitTip5')],
    [t],
  );

  useEffect(() => {
    const iv = setInterval(() => setTip((x) => (x + 1) % tips.length), 6000);
    return () => clearInterval(iv);
  }, [tips.length]);

  useEffect(() => {
    if (!appt?.scheduled_at) return undefined;
    const target = new Date(appt.scheduled_at).getTime();
    const upd = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setCountdown(t('waitStartingNow'));
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
    };
    upd();
    const iv = setInterval(upd, 1000);
    return () => clearInterval(iv);
  }, [appt?.scheduled_at, t]);

  const scheduledLabel = appt?.scheduled_at
    ? new Date(appt.scheduled_at).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
    : '-';

  return (
    <div
      className="min-h-screen bg-hero-gradient flex items-center justify-center p-6"
      data-testid="waiting-room"
    >
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative max-w-lg w-full glass-strong rounded-3xl p-8 text-center shadow-elev-lg"
      >
        <div className="mx-auto h-16 w-16 rounded-2xl bg-primary-gradient text-primary-foreground flex items-center justify-center shadow-glow">
          <Clock className="h-8 w-8" />
        </div>
        <h1 className="mt-5 font-display font-bold text-2xl md:text-3xl">{t('waitTitle')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('waitScheduled', { time: scheduledLabel })}
        </p>

        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            {t('waitCountdown')}
          </div>
          <div
            className="mt-2 font-display font-bold text-5xl md:text-6xl tabular-nums tracking-tight text-primary"
            data-testid="waiting-countdown"
          >
            {countdown || '--:--'}
          </div>
        </div>

        <div className="mt-8 min-h-[80px] flex items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={tip}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-2xl bg-accent/8 border border-accent/20 p-4 text-left flex items-start gap-3"
            >
              <Sparkles className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/80">{tips[tip]}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <Button
          onClick={onCheckAgain}
          variant="outline"
          size="lg"
          className="mt-6 w-full"
          data-testid="waiting-recheck-btn"
        >
          <Loader2 className="h-4 w-4" /> {t('waitCheckAgain')}
        </Button>
        <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1">
          <ShieldCheck className="h-3 w-3 text-accent" /> {t('waitSecurityNote')}
        </p>
      </motion.div>
    </div>
  );
};

export default WaitingRoom;
